import React, { useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { Button } from "components/ui";
import Input from "./Input";
import styles from "./dialog.module.scss";
import { FetchResult, useMutation } from "@apollo/client";
import { MUTATION_ADD_COMPANY_MEMBER } from "apps/company/api/graphQL/mutation";
import appInfo from "utility/app-info";
import { CommonModalProps } from "./common";
import { logError } from "utility/Function";
import { useTranslation } from "lib/i18n/use-translation";

interface AddMemberDialogProps extends CommonModalProps {
  onAddAction: (response: FetchResult) => void;
}
const AddMemberDialog = ({
  onAddAction,
  onCloseAction,
  isOpen,
}: AddMemberDialogProps) => {
  //HOOKS
  const { t } = useTranslation(["admin"]);
  const toggle = () => {
    reset();
    onCloseAction();
  };
  const [addUser] = useMutation(MUTATION_ADD_COMPANY_MEMBER);
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [errorText, setErrorText] = useState<string>("");

  //HANDLERS
  const reset = () => {
    setFirstName("");
    setEmail("");
    setLastName("");
    setErrorText("");
  };

  const handleAddUser = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (firstName && lastName && email) {
      addUser({
        variables: {
          appInfo,
          input: {
            firstName: firstName,
            lastName: lastName,
            email: email,
            timezone: "Europe/London",
          },
        },
      })
        .then((response) => {
          if (response.errors) {
            setErrorText(response.errors[0].message);
          } else if (response.data) {
            toggle();
            onAddAction(response);
            reset();
          }
        })
        .catch((err) => {
          setErrorText(err.message);
          logError("Error adding member", err);
        });
    } else {
      setErrorText(t("admin:members.addMember.requiredFields"));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      style={{ width: "100%", maxWidth: "440px" }}
      className={styles["modal"]}
      contentClassName={styles["modal-content"]}
      centered
    >
      <form onSubmit={handleAddUser}>
        <ModalHeader className={styles["header"]} toggle={toggle}></ModalHeader>
        <ModalBody className={`${styles["body"]} ${styles.okta}`}>
          <p className={styles["title"]}>
            {t("admin:members.addMember.title")}
          </p>
          <p className={`text-small ${styles["description"]}`}>
            {t("admin:members.addMember.description")}
          </p>
          <div className={styles["inputs"]}>
            <Input
              label={t("admin:members.addMember.firstNameLabel")}
              onChange={(e) => setFirstName(e.target.value.trimStart())}
              value={firstName}
              placeholder={t("admin:members.addMember.firstNamePlaceholder")}
              required
            />
            <Input
              label={t("admin:members.addMember.lastNameLabel")}
              text={lastName}
              value={lastName}
              onChange={(e) => setLastName(e.target.value.trimStart())}
              placeholder={t("admin:members.addMember.lastNamePlaceholder")}
              required
            />
            <Input
              label={t("admin:members.addMember.emailLabel")}
              text={email}
              value={email}
              onChange={(e) =>
                setEmail(e.target.value.trimStart().toLowerCase())
              }
              type="email"
              placeholder={t("admin:members.addMember.emailPlaceholder")}
              required
            />
          </div>
          <p className={`sans ${styles["error"]}`}>{errorText}</p>
        </ModalBody>

        <ModalFooter className={styles["footer"]}>
          <Button variant="primary" className="sans" type="submit">
            {t("admin:action.add")}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default AddMemberDialog;
